import { escapeHTML, shortValue, showStatus } from "./ui-utils";

type API = <T>(path: string, options?: RequestInit) => Promise<T>;

type EmailChannel = {
  channelType: string;
  enabled: boolean;
  fromEmail: string;
  fromName: string;
  replyTo?: string;
  hasApiKey: boolean;
  version: number;
  lastTestStatus: string;
  lastTestError?: string;
  lastTestedAt?: string;
};

type EmailDelivery = {
  id: string;
  productName: string;
  subscriptionId: string;
  externalMessageId?: string;
  providerMessageId?: string;
  status: string;
  attemptCount: number;
  providerStatus?: string;
  lastError?: string;
  sentAt?: string;
  createdAt: string;
};

export function initializeEmailConsole(api: API): { load: () => Promise<void> } {
  let configured = false;

  function renderChannel(channel?: EmailChannel): void {
    configured = Boolean(channel);
    document.querySelector<HTMLInputElement>("#email-enabled")!.checked = channel?.enabled || false;
    document.querySelector<HTMLInputElement>("#email-from-email")!.value = channel?.fromEmail || "";
    document.querySelector<HTMLInputElement>("#email-from-name")!.value = channel?.fromName || "";
    document.querySelector<HTMLInputElement>("#email-reply-to")!.value = channel?.replyTo || "";
    document.querySelector<HTMLInputElement>("#email-api-key")!.value = "";
    document.querySelector<HTMLInputElement>("#email-api-key")!.required = !channel?.hasApiKey;
    document.querySelector<HTMLElement>("#email-channel-state")!.textContent = channel
      ? `${channel.enabled ? "ENABLED" : "DISABLED"} · ${channel.lastTestStatus}`
      : "NOT CONFIGURED";
    document.querySelector<HTMLElement>("#email-api-key-help")!.textContent = channel?.hasApiKey
      ? "API Key 已加密保存且不回显；留空表示保持当前 Key，填写表示轮换。"
      : "请输入 Mailchimp Transactional（Mandrill）API Key；平台加密保存且不回显。";
    document.querySelector<HTMLButtonElement>("#test-email-channel")!.disabled = !channel?.hasApiKey;
    const detail = document.querySelector<HTMLElement>("#email-test-detail")!;
    detail.hidden = !channel?.lastTestedAt && !channel?.lastTestError;
    detail.textContent = channel?.lastTestedAt
      ? `最近测试：${new Date(channel.lastTestedAt).toLocaleString("zh-CN")}${channel.lastTestError ? ` · ${channel.lastTestError}` : ""}`
      : channel?.lastTestError || "";
  }

  function renderDeliveries(items: EmailDelivery[]): void {
    document.querySelector<HTMLElement>("#email-delivery-rows")!.innerHTML = items.map(item => `
      <tr>
        <td><strong>${escapeHTML(item.productName)}</strong><small>${escapeHTML(shortValue(item.externalMessageId || item.id, 12))}</small></td>
        <td><code>${escapeHTML(shortValue(item.subscriptionId))}</code></td>
        <td><span class="status-chip ${escapeHTML(item.status.toLowerCase())}">${escapeHTML(item.status)}</span><small>${item.attemptCount} 次尝试${item.providerStatus ? ` · ${escapeHTML(item.providerStatus)}` : ""}${item.providerMessageId ? ` · ${escapeHTML(shortValue(item.providerMessageId, 12))}` : ""}</small></td>
        <td><small>${escapeHTML(new Date(item.sentAt || item.createdAt).toLocaleString("zh-CN"))}</small>${item.lastError ? `<small title="${escapeHTML(item.lastError)}">${escapeHTML(shortValue(item.lastError, 22))}</small>` : ""}</td>
      </tr>
    `).join("");
    document.querySelector<HTMLElement>("#email-delivery-empty")!.hidden = items.length !== 0;
  }

  async function load(): Promise<void> {
    try {
      const [channelBody, deliveryBody] = await Promise.all([
        api<{ configured: boolean; channel?: EmailChannel }>("/v1/provider/email-channel"),
        api<{ deliveries: EmailDelivery[] }>("/v1/provider/email-deliveries?limit=100"),
      ]);
      renderChannel(channelBody.configured ? channelBody.channel : undefined);
      renderDeliveries(deliveryBody.deliveries);
      document.querySelector("#summary-count")!.textContent = `${deliveryBody.deliveries.length} EMAILS`;
    } catch (error) {
      showStatus("email-channel-status", error instanceof Error ? error.message : "邮件通道加载失败", true);
    }
  }

  document.querySelector<HTMLFormElement>("#email-channel-form")!.addEventListener("submit", async event => {
    event.preventDefault();
    try {
      const apiKey = document.querySelector<HTMLInputElement>("#email-api-key")!.value.trim();
      if (!configured && !apiKey) throw new Error("首次配置必须填写 Mailchimp Transactional API Key");
      const body = await api<{ channel: EmailChannel }>("/v1/provider/email-channel", {
        method: "PUT",
        body: JSON.stringify({
          enabled: document.querySelector<HTMLInputElement>("#email-enabled")!.checked,
          apiKey,
          fromEmail: document.querySelector<HTMLInputElement>("#email-from-email")!.value.trim(),
          fromName: document.querySelector<HTMLInputElement>("#email-from-name")!.value.trim(),
          replyTo: document.querySelector<HTMLInputElement>("#email-reply-to")!.value.trim(),
        }),
      });
      renderChannel(body.channel);
      showStatus("email-channel-status", body.channel.enabled
        ? "邮件通道已启用。"
        : "邮件通道已保存。请测试连接；通过后再次保存并启用自动发送。");
    } catch (error) {
      showStatus("email-channel-status", error instanceof Error ? error.message : "邮件通道保存失败", true);
    }
  });

  document.querySelector<HTMLButtonElement>("#test-email-channel")!.addEventListener("click", async () => {
    try {
      const body = await api<{ channel: EmailChannel }>("/v1/provider/email-channel/test", { method: "POST" });
      renderChannel(body.channel);
      showStatus("email-channel-status", "Mailchimp Transactional 连接测试通过；本测试不会发送邮件。");
    } catch (error) {
      showStatus("email-channel-status", error instanceof Error ? error.message : "邮件通道测试失败", true);
      await load();
    }
  });

  return { load };
}
